import ArrowForwardOutlined from '@icons/ArrowForwardOutlined';
import { Box, Fab, Typography } from '@mui/material';
import { openPanel, setDetailPanelActiveTab, setTackleboxHistoryTab, togglePanelCampaignCreator } from '@slice';
import { TackleboxShareLink, UserNotification } from '@types';
import dayjs from 'dayjs';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { extractNameFromEmail } from '../../utils';

const isTackleboxShareLink = (obj: unknown): obj is TackleboxShareLink => {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return typeof o.tacklebox_id === 'string' && typeof o.name === 'string' && typeof o.shared_by === 'string';
};

const smallText = { color: 'text.secondary', fontWeight: 400, fontSize: '12px', lineHeight: '166%', letterSpacing: '0px' };

export interface NavbarMailboxTackleboxShareNotificationProps {
  notification: UserNotification;
  onNavigate: (id: string) => void;
}

const NavbarMailboxTackleboxShareNotification = ({ notification, onNavigate }: NavbarMailboxTackleboxShareNotificationProps) => {
  const dispatch = useDispatch();
  const isPanelCampaignCreatorOpen = useSelector((state: RootState) => state.slice.isPanelCampaignCreatorOpen);

  const data = useMemo(() => (isTackleboxShareLink(notification.data) ? notification.data : null), [notification.data]);
  const createdAtRelative = useMemo(() => dayjs(notification.created_at).fromNow(), [notification.created_at]);

  const text = useMemo(() => {
    if (!data) return notification.message;
    const base = `${extractNameFromEmail(data.shared_by)} shared the TiTo job "${data.name}" with you`;
    return notification.message ? `${base} with message: "${notification.message}".` : `${base}.`;
  }, [data, notification.message]);

  // Open the TiTo panel on the Shared Jobs tab, the same way the navbar opens TiTo
  const onClicked = useCallback(() => {
    dispatch(setTackleboxHistoryTab('shared'));
    dispatch(setDetailPanelActiveTab('tito'));
    dispatch(openPanel());
    if (isPanelCampaignCreatorOpen) {
      dispatch(togglePanelCampaignCreator());
    }
    onNavigate(notification.id);
  }, [dispatch, isPanelCampaignCreatorOpen, notification.id, onNavigate]);

  return (
    <Box sx={{ display: 'flex', p: 2, flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ ...smallText, flex: 1 }}>Shared TiTo job</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
          <Typography sx={smallText}>{createdAtRelative}</Typography>
          {notification.seen ? (
            <Box sx={{ width: 8 }} />
          ) : (
            <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: 'primary.main' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <Typography sx={{ flex: 1, fontWeight: 400, fontSize: '14px', letterSpacing: '0px', lineHeight: '143%' }}>{text}</Typography>
        <Fab size="small" color="primary" aria-label="Open shared jobs" sx={{ color: 'white' }} onClick={onClicked}>
          <ArrowForwardOutlined />
        </Fab>
      </Box>
    </Box>
  );
};

export default memo(NavbarMailboxTackleboxShareNotification);
